import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { historyApi, type MeStats } from '@/api/history';
import { CasinoButton } from '@/components/CasinoButton';
import { LedNumber } from '@/components/LedNumber';
import { useMeBootstrap } from '@/hooks/useMeBootstrap';
import { useSession } from '@/state/session';
import { formatDollars } from '@/lib/payouts';
import { cn } from '@/lib/utils';

export function Me() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const query = useMeBootstrap();
  const user = useSession((s) => s.user);
  const setUser = useSession((s) => s.setUser);

  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');

  // Redirect only after the bootstrap query has actually CONFIRMED an
  // anonymous session. Never redirect on initial 'unknown' status or while
  // the first /me fetch is still in flight — that's the bounce-to-login bug.
  const confirmedAnonymous =
    query.isFetched && query.isSuccess && query.data?.user === null && user === null;

  useEffect(() => {
    if (confirmedAnonymous) navigate('/login');
  }, [confirmedAnonymous, navigate]);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
      setPhone(user.phone ?? '');
    }
  }, [user]);

  const update = useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: ({ user: u }) => {
      const merged = { ...u, sessionElevated: user?.sessionElevated };
      setUser(merged);
      qc.setQueryData(['me'], { user: merged });
      setEditing(false);
    },
  });

  const logout = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      setUser(null);
      qc.setQueryData(['me'], { user: null });
      navigate('/login');
    },
  });

  if (!user) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-5">
        <p className="text-cabinet-cream/60">Loading…</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md px-5 py-8 space-y-6">
      <h1 className="font-display text-cabinet-gold text-2xl uppercase tracking-widest">
        Profile
      </h1>

      <ApprovalBanner approved={user.isApproved} />

      {user.isApproved && <StatsCard />}

      <section className="cabinet-card p-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-cabinet-gold text-2xl uppercase tracking-widest">
            Your profile
          </h2>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-sm text-cabinet-gold underline-offset-2 hover:underline"
            >
              Edit
            </button>
          )}
        </div>

        {!editing ? (
          <dl className="space-y-3 text-sm">
            <Row label="Email" value={user.email} />
            <Row label="Display name" value={user.displayName || '— not set —'} />
            <Row label="Phone" value={user.phone || '— none —'} />
            <Row
              label="Role"
              value={
                user.isAdmin
                  ? user.sessionElevated
                    ? 'Admin (elevated this session)'
                    : 'Admin (re-enter password to unlock)'
                  : 'Player'
              }
            />
            <Row label="Status" value={user.isApproved ? 'Approved' : 'Pending approval'} />
          </dl>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate({
                displayName: displayName.trim(),
                phone: phone.trim() || undefined,
              });
            }}
            className="space-y-3"
          >
            <Field label="Display name">
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                className={inputCls}
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                className={inputCls}
              />
            </Field>
            <div className="flex gap-2">
              <CasinoButton type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </CasinoButton>
              <CasinoButton type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </CasinoButton>
            </div>
            {update.isError && (
              <p className="text-sm text-cabinet-flame-orange">
                Couldn't save. Try again.
              </p>
            )}
          </form>
        )}
      </section>

      <Link
        to="/tournament/active"
        className="block rounded-xl border-2 border-cabinet-flame-orange bg-cabinet-purple-deep px-4 py-3 text-center font-display uppercase tracking-widest text-cabinet-flame-orange hover:bg-cabinet-purple"
      >
        Active tournament →
      </Link>

      {user.isAdmin && user.sessionElevated && (
        <Link
          to="/admin"
          className="block rounded-xl border-2 border-cabinet-gold bg-cabinet-purple-deep px-4 py-3 text-center font-display uppercase tracking-widest text-cabinet-gold hover:bg-cabinet-purple"
        >
          Admin tools →
        </Link>
      )}

      <button
        type="button"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className="block w-full text-center text-sm text-cabinet-cream/50 hover:text-cabinet-flame-orange py-3"
      >
        Sign out
      </button>
    </main>
  );
}

function ApprovalBanner({ approved }: { approved: boolean }) {
  if (approved) return null;
  return (
    <div className="rounded-xl border-2 border-cabinet-gold bg-cabinet-purple-deep px-4 py-3 text-sm leading-snug">
      <p className="font-display uppercase tracking-widest text-cabinet-gold text-xs mb-1">
        Pending approval
      </p>
      <p className="text-cabinet-cream/80">
        An admin will approve your account before you can join tournaments. You can still
        update your profile while you wait.
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-cabinet-cream/10 pb-2 last:border-b-0">
      <dt className="text-cabinet-cream/60 uppercase tracking-widest text-xs">{label}</dt>
      <dd className="text-cabinet-cream text-right">{value}</dd>
    </div>
  );
}

const inputCls =
  'w-full rounded-md bg-black/50 border-2 border-cabinet-cream/20 px-3 py-2.5 text-cabinet-cream focus:border-cabinet-gold focus:outline-none focus:ring-2 focus:ring-cabinet-gold/40 transition';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function StatsCard() {
  const q = useQuery({
    queryKey: ['me', 'stats'],
    queryFn: historyApi.myStats,
    staleTime: 60_000,
  });
  if (q.isLoading) {
    return (
      <section className="cabinet-card p-5 text-center">
        <p className="text-cabinet-cream/60 text-sm">Loading stats…</p>
      </section>
    );
  }
  if (!q.data) return null;
  return <StatsCardInner stats={q.data} />;
}

function StatsCardInner({ stats }: { stats: MeStats }) {
  const hasActivity = stats.totalEntries > 0 || stats.placementCount > 0;
  return (
    <section className="cabinet-card p-5 space-y-4">
      <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
        Your stats
      </h2>

      {!hasActivity ? (
        <p className="text-sm text-cabinet-cream/60">
          You haven't joined any tournaments yet.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Winnings">
              <LedNumber
                value={Math.round(stats.totalWinningsCents / 100)}
                currency
                digits={4}
                size="md"
              />
            </Stat>
            <Stat label="Spent">
              <p className="font-display text-cabinet-cream text-xl tabular-nums">
                {formatDollars(stats.totalSpentCents)}
              </p>
            </Stat>
            <Stat label="Net">
              <p
                className={cn(
                  'font-display text-xl tabular-nums',
                  stats.netCents > 0
                    ? 'text-cabinet-gold-bright'
                    : stats.netCents < 0
                      ? 'text-cabinet-flame-orange'
                      : 'text-cabinet-cream',
                )}
              >
                {stats.netCents >= 0 ? '+' : ''}
                {formatDollars(stats.netCents)}
              </p>
            </Stat>
            <Stat label="Wins / placements">
              <p className="font-display text-cabinet-cream text-xl tabular-nums">
                {stats.winCount}{' '}
                <span className="text-cabinet-cream/50 text-sm">
                  / {stats.placementCount}
                </span>
              </p>
            </Stat>
          </div>

          {stats.uncollectedCents > 0 && (
            <div className="rounded-md bg-cabinet-flame-orange/10 border border-cabinet-flame-orange/40 px-3 py-2 text-sm text-cabinet-cream">
              <span className="font-display tracking-widest uppercase text-xs text-cabinet-flame-orange">
                Uncollected
              </span>{' '}
              · {formatDollars(stats.uncollectedCents)} owed to you
            </div>
          )}

          {stats.placements.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-display text-cabinet-gold-bright text-sm uppercase tracking-widest">
                Placements
              </h3>
              <ul className="space-y-1.5">
                {stats.placements.map((p) => (
                  <li key={p.tournamentId}>
                    <Link
                      to={`/history/${p.tournamentId}`}
                      className="flex items-center justify-between gap-3 rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 hover:ring-cabinet-gold/40 transition px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-cabinet-cream truncate">
                          {p.tournamentName}
                        </p>
                        <p className="text-[10px] text-cabinet-cream/50">
                          {p.completedAt
                            ? new Date(p.completedAt).toLocaleDateString()
                            : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <p
                          className={cn(
                            'font-display uppercase tracking-widest text-xs',
                            p.place === 1
                              ? 'text-cabinet-gold-bright'
                              : 'text-cabinet-cream',
                          )}
                        >
                          {ordinal(p.place)}
                        </p>
                        <p className="font-display text-cabinet-gold-bright text-base tabular-nums">
                          {formatDollars(p.amountCents)}
                        </p>
                        <p
                          className={cn(
                            'text-[10px] uppercase tracking-widest',
                            p.paid ? 'text-cabinet-gold-bright' : 'text-cabinet-flame-orange',
                          )}
                        >
                          {p.paid ? 'Paid' : 'Uncollected'}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md bg-cabinet-purple-ink/40 ring-1 ring-cabinet-cream/10 px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">
        {label}
      </p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
